
import { Component, Vue } from 'vue-property-decorator';

@Component({})
export default class KnowledgeBaseCorrectionPage extends Vue {
  public markdownContent: string = '';
  public isLoading: boolean = false;
  public isSuccess: boolean = false;
  public isError: boolean = false;
  public successMessage: string = '';
  public errorMessage: string = '';
  public questionLookup: string = '';
  public searchLoading: boolean = false;
  public searchMessage: string = '';
  public searchMessageType: 'success' | 'info' | 'error' = 'info';
  public allCorrections: any[] = [];
  public currentlyLoadedIndex: number = -1;
  public removeDialog: boolean = false;
  public removeLoading: boolean = false;
  public correctionToRemove: any = null;
  public downloadLoading: boolean = false;
  public aiRewriteLoading: boolean = false;
  public aiPreviewDialog: boolean = false;
  public aiMarkdownPreview: string = '';
  private _originalMarkdown: string = '';
  public hasAiRewritePending: boolean = false;

  private ATLAS_API_URL = process.env.VUE_APP_ATLAS_API_URL || 'http://localhost:8000';

  public async submitCorrection() {
    // Basic validation
    if (!this.markdownContent || this.markdownContent.trim() === '') {
      this.showError('Please enter the Q&A markdown content');
      return;
    }

    // Parse the markdown to extract fields
    const parsed = this.parseMarkdown(this.markdownContent);

    if (!parsed.valid) {
      this.showError(parsed.error || 'Invalid markdown format. Please check the format guide.');
      return;
    }

    this.isLoading = true;
    this.isError = false;
    this.isSuccess = false;

    try {
      // Get auth token from localStorage
      const token: string = localStorage.getItem('token') || '';

      // Call Atlas API to add correction
      const fetchResponse = await fetch(
        `${this.ATLAS_API_URL}/corrections/add`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
          },
          body: JSON.stringify({
            question: parsed.question,
            answer: parsed.answer,
            keywords: parsed.keywords,
            also_answers: parsed.alsoAnswers,
            out_of_scope: parsed.outOfScope,
            admin_email: 'admin@assisty.com',
          }),
        }
      );

      if (!fetchResponse.ok) {
        if (fetchResponse.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        } else if (fetchResponse.status === 503) {
          throw new Error('OpenAI service unavailable. Please try again later.');
        } else {
          const errorData = await fetchResponse.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to submit correction. Please try again.');
        }
      }

      const response: any = await fetchResponse.json();
      this.showSuccess(`✅ Correction added successfully! (${response.corrections_count} total corrections)`);
      this.clearForm();
    } catch (error: any) {
      console.error('Failed to submit correction:', error);
      this.showError(error.message || 'Failed to submit correction. Please try again.');
    } finally {
      this.isLoading = false;
    }
  }

  public async searchCorrection() {
    if (!this.questionLookup.trim()) {
      this.showError('Please enter a question to search');
      return;
    }

    this.searchLoading = true;
    this.searchMessage = '';
    this.allCorrections = [];
    this.currentlyLoadedIndex = -1;

    try {
      // Get auth token from localStorage
      const token: string = localStorage.getItem('token') || '';

      // Build URL with query params
      const url = `${this.ATLAS_API_URL}/corrections/search?question=${encodeURIComponent(this.questionLookup)}&limit=10`;

      const fetchResponse = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
      });

      if (!fetchResponse.ok) {
        throw new Error(`Search failed: ${fetchResponse.statusText}`);
      }

      const response: any = await fetchResponse.json();

      if (response.found && response.corrections && response.corrections.length > 0) {
        // Store all corrections
        this.allCorrections = response.corrections;

        // Load first correction into form
        const firstCorrection = response.corrections[0];
        this.markdownContent = this.formatCorrectionAsMarkdown(firstCorrection);
        this.currentlyLoadedIndex = 0;

        // Show success message with count
        this.searchMessage = `Found ${response.count} related correction${response.count > 1 ? 's' : ''}`;
        this.searchMessageType = 'success';
      } else {
        // No results found
        this.searchMessage = 'No existing correction found. Please create new content.';
        this.searchMessageType = 'info';
        this.markdownContent = '';
        this.allCorrections = [];
        this.currentlyLoadedIndex = -1;
      }
    } catch (error: any) {
      this.showError(`Search failed: ${error.message || 'Unknown error'}`);
      this.allCorrections = [];
      this.currentlyLoadedIndex = -1;
    } finally {
      this.searchLoading = false;
    }
  }

  public clearForm() {
    this.markdownContent = '';
    this.questionLookup = '';
    this.searchMessage = '';
    this.isError = false;
    this.isSuccess = false;
    // Reset AI rewrite state
    this._originalMarkdown = '';
    this.aiMarkdownPreview = '';
    this.hasAiRewritePending = false;
  }

  public formatCorrectionAsMarkdown(correction: any): string {
    let markdown = `---\n\n# Question: ${correction.question}\n\n`;

    if (correction.keywords && correction.keywords.length > 0) {
      markdown += `**Keywords**: ${correction.keywords.join(', ')}\n\n`;
    }

    markdown += `**Answer**:\n${correction.answer}\n\n`;

    if (correction.also_answers && correction.also_answers.length > 0) {
      markdown += `**Also Answers**:\n`;
      correction.also_answers.forEach((alt: string) => {
        markdown += `- ${alt}\n`;
      });
      markdown += '\n';
    }

    if (correction.out_of_scope && correction.out_of_scope.length > 0) {
      markdown += `**Out-of-scope**:\n`;
      correction.out_of_scope.forEach((scope: string) => {
        markdown += `- ${scope}\n`;
      });
      markdown += '\n';
    }

    markdown += `**Last Updated**: ${correction.last_updated || new Date().toISOString().split('T')[0]}\n\n---`;

    return markdown;
  }

  public loadCorrectionToForm(correction: any, index: number) {
    // Load correction into form
    this.markdownContent = this.formatCorrectionAsMarkdown(correction);

    // Update tracking
    this.currentlyLoadedIndex = index;

    // Scroll to top of form
    this.$vuetify.goTo(0, { duration: 300, offset: 0, easing: 'easeInOutCubic' });
  }

  public loadExample() {
    this.markdownContent = `---

# Question: Multi-store stock combination settings

**Keywords**: multi-store settings, combine store data, sum stock across stores, master store inventory, connected stores, stock combination, disabled option single source, combine new store data button, approve connection request

**Answer**:
To combine stock data from multiple stores in Assisty:

**Connect the Stores:**
1. Go to **Adjust Settings > Multi-store settings**
2. Click on **Combine New Store Data** and enter the store URL (requires Assisty app installed in that store)
3. Send the connection request for approval
4. **IMPORTANT**: Click on the approve action to view & approve in the connected store

**Stock Combination Settings:**
Use the "Sum stock across stores" toggle:
- **Enabled**: Stock quantities are summed across the master store and all connected stores. Each SKU will show total inventory from all locations combined
- **Disabled**: Only the master store's stock is considered, ignoring connected store inventory. This is the best option when you have single source inventory for all stores

**Important Notes:**
- There is **NO inventory movement or transfer feature** between stores in Assisty
- The system only aggregates stock data for reporting purposes
- All connected stores must have Assisty app installed
- Approval must be done from the connected store side

After connecting, all stock data will be aggregated and viewable from your master store's dashboard, showing total stock levels for each product across all locations.

**Also Answers**:
- How to combine stock from multiple stores in Assisty?
- What does disabled mean in multi-store settings?
- How do I connect multiple Shopify stores?
- Can I transfer inventory between stores?
- Multi-store inventory configuration

**Out-of-scope**:
- Do not answer questions about inventory transfer features (not available)
- Do not answer questions about physical inventory movement between stores
- Redirect questions about Shopify Plus multi-location inventory to Shopify documentation
- Do not provide recommendations on which setting to choose for specific business models

**Last Updated**: 2025-01-15

---`;
  }

  private parseMarkdown(content: string): {
    valid: boolean;
    question?: string;
    answer?: string;
    keywords?: string[];
    alsoAnswers?: string[];
    outOfScope?: string[];
    error?: string;
  } {
    try {
      // Extract Question
      const questionMatch = content.match(/# Question:\s*(.+)/i);
      if (!questionMatch) {
        return { valid: false, error: 'Question not found. Format: # Question: [topic]' };
      }
      const question = questionMatch[1].trim();

      // Extract Keywords
      const keywordsMatch = content.match(/\*\*Keywords\*\*:\s*(.+)/i);
      if (!keywordsMatch) {
        return { valid: false, error: 'Keywords not found. Format: **Keywords**: a, b, c' };
      }
      const keywords = keywordsMatch[1].split(',').map((k) => k.trim()).filter((k) => k);

      // Extract Answer
      const answerMatch = content.match(/\*\*Answer\*\*:\s*\n([\s\S]+?)(?=\n\*\*Also Answers\*\*:|\n\*\*Out-of-scope\*\*:|\n\*\*Last Updated\*\*:|$)/i);
      if (!answerMatch) {
        return { valid: false, error: 'Answer not found. Format: **Answer**: [answer text]' };
      }
      const answer = answerMatch[1].trim();

      // Extract Also Answers (optional)
      const alsoAnswersSection = content.match(/\*\*Also Answers\*\*:\s*\n([\s\S]+?)(?=\n\*\*Out-of-scope\*\*:|\n\*\*Last Updated\*\*:|---|\n\n#|$)/i);
      let alsoAnswers: string[] = [];
      if (alsoAnswersSection) {
        alsoAnswers = alsoAnswersSection[1]
          .split('\n')
          .map((line) => line.replace(/^-\s*/, '').trim())
          .filter((line) => line && !line.startsWith('**'));
      }

      // Extract Out-of-scope (optional)
      const outOfScopeSection = content.match(/\*\*Out-of-scope\*\*:\s*\n([\s\S]+?)(?=\n\*\*Last Updated\*\*:|---|\n\n#|$)/i);
      let outOfScope: string[] = [];
      if (outOfScopeSection) {
        outOfScope = outOfScopeSection[1]
          .split('\n')
          .map((line) => line.replace(/^-\s*/, '').trim())
          .filter((line) => line && !line.startsWith('**'));
      }

      return {
        valid: true,
        question,
        answer,
        keywords,
        alsoAnswers,
        outOfScope,
      };
    } catch (error) {
      return { valid: false, error: 'Failed to parse markdown. Please check the format.' };
    }
  }

  public confirmRemoveCorrection(correction: any) {
    this.correctionToRemove = correction;
    this.removeDialog = true;
  }

  public async removeCorrection() {
    if (!this.correctionToRemove) {
      return;
    }

    this.removeLoading = true;
    this.isError = false;
    this.isSuccess = false;

    try {
      // Get auth token from localStorage
      const token: string = localStorage.getItem('token') || '';

      // Call Atlas API to remove correction - send only the question
      const fetchResponse = await fetch(
        `${this.ATLAS_API_URL}/corrections/remove`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
          },
          body: JSON.stringify({
            question: this.correctionToRemove.question,
            admin_email: 'admin@assisty.com',
          }),
        }
      );

      if (!fetchResponse.ok) {
        if (fetchResponse.status === 404) {
          throw new Error('Correction not found.');
        } else if (fetchResponse.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        } else {
          const errorData = await fetchResponse.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to remove correction. Please try again.');
        }
      }

      this.showSuccess(`✅ Correction "${this.correctionToRemove.question}" removed successfully!`);

      // Close dialog
      this.removeDialog = false;
      this.correctionToRemove = null;

      // Re-search to refresh the list
      if (this.questionLookup) {
        await this.searchCorrection();
      } else {
        // Clear the list if no search query
        this.allCorrections = [];
        this.currentlyLoadedIndex = -1;
        this.markdownContent = '';
      }
    } catch (error: any) {
      console.error('Failed to remove correction:', error);
      this.showError(error.message || 'Failed to remove correction. Please try again.');
    } finally {
      this.removeLoading = false;
    }
  }

  public async downloadKnowledgeBase() {
    this.downloadLoading = true;
    this.isError = false;

    try {
      // Get auth token from localStorage (same as http service)
      const token: string = localStorage.getItem('token') || '';

      // Call Atlas API to download the markdown file
      const response = await fetch(
        `${this.ATLAS_API_URL}/corrections/download`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`);
      }

      // Get the markdown content
      const blob = await response.blob();

      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'admin_corrections.md';
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      this.showSuccess('✅ Knowledge base downloaded successfully!');
    } catch (error: any) {
      console.error('Failed to download knowledge base:', error);
      this.showError(error.message || 'Failed to download knowledge base. Please try again.');
    } finally {
      this.downloadLoading = false;
    }
  }

  public async aiRewriteCorrection() {
    if (!this.markdownContent || this.markdownContent.trim() === '') {
      this.showError('Please enter some content to rewrite');
      return;
    }

    this.aiRewriteLoading = true;
    this.isError = false;
    this.isSuccess = false;

    try {
      // Get auth token from localStorage
      const token: string = localStorage.getItem('token') || '';

      // Call Atlas API to AI rewrite the content
      const fetchResponse = await fetch(
        `${this.ATLAS_API_URL}/corrections/ai-rewrite`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
          },
          body: JSON.stringify({
            content: this.markdownContent,
            admin_email: 'admin@assisty.com',
          }),
        }
      );

      if (!fetchResponse.ok) {
        if (fetchResponse.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        } else if (fetchResponse.status === 503) {
          throw new Error('AI service unavailable. Please try again later.');
        } else {
          const errorData = await fetchResponse.json().catch(() => ({}));
          throw new Error(errorData.message || 'AI rewrite failed. Please try again.');
        }
      }

      const response: any = await fetchResponse.json();

      if (response.success && response.markdown) {
        // Store the original content as backup
        this._originalMarkdown = this.markdownContent;
        // Store AI response in preview field
        this.aiMarkdownPreview = response.markdown;
        // Open preview dialog for user confirmation
        this.aiPreviewDialog = true;
      } else {
        throw new Error('Invalid response from AI service');
      }
    } catch (error: any) {
      console.error('Failed to AI rewrite correction:', error);
      this.showError(error.message || 'AI rewrite failed. Please try again.');
    } finally {
      this.aiRewriteLoading = false;
    }
  }

  public applyAiRewrite() {
    // Apply the AI-generated content to the main field
    this.markdownContent = this.aiMarkdownPreview;
    // Enable undo functionality
    this.hasAiRewritePending = true;
    // Close the preview dialog
    this.aiPreviewDialog = false;
    this.showSuccess('✨ AI rewrite applied! You can undo this change or submit when ready.');
  }

  public cancelAiRewrite() {
    // Close the preview dialog without applying changes
    this.aiPreviewDialog = false;
    // Clear the preview content
    this.aiMarkdownPreview = '';
    // Clear the backup since user cancelled
    this._originalMarkdown = '';
  }

  public undoAiRewrite() {
    if (this._originalMarkdown) {
      // Restore the original content
      this.markdownContent = this._originalMarkdown;
      // Clear the backup
      this._originalMarkdown = '';
      // Disable undo button
      this.hasAiRewritePending = false;
      // Clear preview content
      this.aiMarkdownPreview = '';
      this.showSuccess('↩️ AI rewrite undone. Original content restored.');
    }
  }

  private showSuccess(message: string) {
    this.successMessage = message;
    this.isSuccess = true;
    setTimeout(() => {
      this.isSuccess = false;
    }, 5000);
  }

  private showError(message: string) {
    this.errorMessage = message;
    this.isError = true;
  }
}
